import type { SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import type { TFunction } from 'i18next';

import { IAjudaDuvida, TAjudaDuvidaId, TAjudaTela } from './ajudaConteudo';

/**
 * A Ajuda organizada por TAREFA: o que a pessoa quer fazer, e as dúvidas daquilo.
 *
 * O manual tinha sete abas, e duas delas ("As telas" e "Dúvidas frequentes") falavam das
 * MESMAS telas por recortes diferentes: quem queria resolver uma coisa precisava saber em
 * qual aba a resposta tinha sido escrita. A tarefa é o que a pessoa sabe antes de abrir a
 * Ajuda ("vou começar o dia", "preciso reprogramar"), então é por ela que o manual abre.
 *
 * Não há filtro por papel, de propósito: o "quem faz" continua escrito DENTRO de cada
 * dúvida, como informação, e nunca esconde uma resposta de quem está procurando.
 */

/** As seis tarefas, na ordem do dia: o que acontece agora primeiro, o ajuste por último. */
const TAREFAS = ['comecarODia', 'programar', 'pendencias', 'medirTempos', 'acompanhar', 'configurar'] as const;

export type TAjudaTarefa = (typeof TAREFAS)[number];

export const AJUDA_TAREFAS: ReadonlyArray<TAjudaTarefa> = TAREFAS;

/** O ícone do atalho de cada tarefa. É código, e não tradução: a figura não muda de idioma. */
const ICONE_DA_TAREFA: Readonly<Record<TAjudaTarefa, SymbolCodepoints>> = {
  comecarODia: 'today',
  programar: 'calendar_clock',
  pendencias: 'build',
  medirTempos: 'timer',
  acompanhar: 'monitoring',
  configurar: 'tune',
};

/** Onde uma dúvida aparece: as tarefas que a listam e as telas onde ela se resolve. */
interface IOndeMoraADuvida {
  tarefas: ReadonlyArray<TAjudaTarefa>;
  telas: ReadonlyArray<TAjudaTela>;
}

/**
 * A LISTA EDITÁVEL: uma linha por dúvida, dizendo em que tarefas ela entra e em que telas
 * ela vive.
 *
 * Uma dúvida pode estar em mais de uma tarefa de propósito ("Errei uma alteração, como
 * desfaço?" é pendência e é configuração), e em mais de uma tela quando a resposta passa
 * por duas ("por que a OP foi parar nesta célula" se lê na ficha e se resolve no Mapa). O
 * mapa é um `Record` de TODAS as dúvidas, e não uma lista por tarefa, porque assim
 * esquecer uma pergunta nova é erro de compilação em vez de uma resposta que ninguém acha.
 *
 * As telas são as do menu de HOJE: as três do tempo por peça são abas de Tempos, e a
 * Visão executiva é aba do Início.
 */
const ONDE_MORA: Readonly<Record<TAjudaDuvidaId, IOndeMoraADuvida>> = {
  hoje: { tarefas: ['comecarODia'], telas: ['inicio'] },
  presenca: { tarefas: ['comecarODia'], telas: ['apontamento'] },
  apontarHora: { tarefas: ['comecarODia'], telas: ['apontamento'] },
  // A mesma tarefa e a mesma tela de apontar a hora: para a líder da jaqueta, ESTE é o
  // apontamento do dia dela, e não um assunto à parte.
  apontarPorEtapa: { tarefas: ['comecarODia'], telas: ['apontamento'] },
  saldo: { tarefas: ['comecarODia'], telas: ['apontamento', 'ops'] },
  conserto: { tarefas: ['comecarODia', 'pendencias'], telas: ['apontamento'] },
  conferirMarca: { tarefas: ['comecarODia', 'acompanhar'], telas: ['apontamento'] },
  mpPrevista: { tarefas: ['pendencias'], telas: ['fila'] },
  cobrarMp: { tarefas: ['pendencias'], telas: ['fila'] },
  checklist: { tarefas: ['pendencias'], telas: ['ops'] },
  maquinaParada: { tarefas: ['pendencias'], telas: ['cadastros'] },
  balancear: { tarefas: ['programar'], telas: ['balanceamento'] },
  participacao: { tarefas: ['configurar'], telas: ['cadastros'] },
  polivalencia: { tarefas: ['configurar'], telas: ['cadastros'] },
  decidirFila: { tarefas: ['comecarODia', 'pendencias'], telas: ['fila'] },
  dataMudou: { tarefas: ['programar', 'acompanhar'], telas: ['ops', 'mapa'] },
  celulaSugerida: { tarefas: ['programar'], telas: ['ops', 'mapa'] },
  // As seis do N74. O modo e a janela são parâmetro (quem configura); o que o sistema
  // nunca move, a validação, o arraste e a troca de célula são trabalho de quem programa.
  modoProgramacao: { tarefas: ['configurar', 'programar'], telas: ['settings'] },
  janelaCongelada: { tarefas: ['configurar'], telas: ['settings'] },
  nuncaMoveSozinho: { tarefas: ['programar'], telas: ['mapa', 'ops'] },
  opValidada: { tarefas: ['programar'], telas: ['mapa', 'ops'] },
  arrastarNoMapa: { tarefas: ['programar'], telas: ['mapa'] },
  trocarCelula: { tarefas: ['programar'], telas: ['ops', 'mapa'] },
  // As seis do N76. O botão vive no Mapa, então elas são trabalho de quem programa; o
  // objetivo é parâmetro, e desfazer a proposta aplicada se resolve no histórico.
  botaoOtimizar: { tarefas: ['programar'], telas: ['mapa'] },
  objetivoDaOtimizacao: { tarefas: ['configurar', 'programar'], telas: ['settings', 'mapa'] },
  otimizacaoNuncaMexe: { tarefas: ['programar'], telas: ['mapa'] },
  propostaEMelhor: { tarefas: ['programar'], telas: ['mapa'] },
  aceitarParteDaProposta: { tarefas: ['programar'], telas: ['mapa'] },
  desfazerProposta: { tarefas: ['programar', 'pendencias'], telas: ['mapa', 'settings'] },
  desfazer: { tarefas: ['pendencias', 'configurar'], telas: ['settings'] },
  aprovar: { tarefas: ['configurar'], telas: ['settings', 'inicio'] },
  simular: { tarefas: ['programar'], telas: ['simulador'] },
  importar: { tarefas: ['programar', 'acompanhar'], telas: ['ops'] },
  // O outro lado da importação: o arquivo entra todo dia e precisa voltar no mesmo dia.
  devolverDatas: { tarefas: ['programar', 'acompanhar'], telas: ['ops', 'inicio'] },
  // O cinza é "falta tempo por peça ou célula", então ele é pergunta de quem acompanha e
  // trabalho de quem mede: aparece nas duas tarefas.
  cinza: { tarefas: ['acompanhar', 'medirTempos'], telas: ['ops', 'mapa'] },
  datasLonge: { tarefas: ['acompanhar', 'medirTempos'], telas: ['inicio', 'ops'] },
  tempos: { tarefas: ['medirTempos'], telas: ['curadoria', 'sequencias', 'cronometragem'] },
  entenderNumero: { tarefas: ['acompanhar'], telas: ['inicio', 'ops', 'relatorios'] },
  filtrar: { tarefas: ['acompanhar'], telas: ['ops', 'fila', 'relatorios'] },
  portal: { tarefas: ['acompanhar'], telas: ['portal'] },
  feriados: { tarefas: ['configurar'], telas: ['settings'] },
  relatorios: { tarefas: ['acompanhar'], telas: ['relatorios'] },
  faltaValidar: { tarefas: ['configurar'], telas: ['inicio', 'settings'] },
  verFicha: { tarefas: ['comecarODia'], telas: ['apontamento'] },
  // Subir a sequência por planilha é trabalho de quem mede o tempo, e a tela é a mesma
  // onde ela é validada depois.
  subirSequenciaPlanilha: { tarefas: ['medirTempos'], telas: ['sequencias'] },
  // As quatro do ritmo por produto. O cadastro é base, mas informar o ritmo é trabalho de
  // quem MEDE o tempo: a ficha do produto é onde o número do balanceamento entra, e é por
  // ela que se pergunta qual dos dois vale.
  cadastrarProdutos: { tarefas: ['configurar'], telas: ['cadastros'] },
  informarRitmo: { tarefas: ['medirTempos'], telas: ['cadastros', 'cronometragem'] },
  ritmoOuTempoInformado: { tarefas: ['medirTempos'], telas: ['cadastros', 'curadoria'] },
  // O parâmetro é de configuração, mas quem sente o efeito é quem programa a data.
  produtividadeSobreRitmo: { tarefas: ['configurar', 'programar'], telas: ['settings'] },
};

export interface IAjudaTarefa {
  id: TAjudaTarefa;
  icone: SymbolCodepoints;
  nome: string;
  /** Uma linha dizendo o que cabe nesta tarefa, para a escolha não ser às cegas. */
  resumo: string;
  duvidas: Array<IAjudaDuvida>;
}

/** As telas em que uma dúvida aparece. */
export function telasDaDuvida(id: TAjudaDuvidaId): ReadonlyArray<TAjudaTela> {
  return ONDE_MORA[id].telas;
}

/**
 * As dúvidas de UMA tela, na ordem do manual.
 *
 * Recebe a lista já montada em vez de montá-la: a tela, a Ajuda inteira e o "?" leem as
 * MESMAS dúvidas, e uma segunda montagem seria uma segunda redação a divergir da primeira.
 */
export function duvidasDaTela(duvidas: Array<IAjudaDuvida>, tela: TAjudaTela): Array<IAjudaDuvida> {
  return duvidas.filter((duvida) => ONDE_MORA[duvida.id].telas.includes(tela));
}

/** As dúvidas de UMA tarefa, na ordem do manual. */
export function duvidasDaTarefa(duvidas: Array<IAjudaDuvida>, tarefa: TAjudaTarefa): Array<IAjudaDuvida> {
  return duvidas.filter((duvida) => ONDE_MORA[duvida.id].tarefas.includes(tarefa));
}

/** As seis tarefas com as dúvidas de cada uma, prontas para a tela. */
export function buildTarefas(duvidas: Array<IAjudaDuvida>, t: TFunction): Array<IAjudaTarefa> {
  return TAREFAS.map((id) => ({
    id,
    icone: ICONE_DA_TAREFA[id],
    nome: t(`ajuda.tarefas.item.${id}.nome`),
    resumo: t(`ajuda.tarefas.item.${id}.resumo`),
    duvidas: duvidasDaTarefa(duvidas, id),
  }));
}
